import React, { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function MaterialsPage() {
  const [activeCategory, setActiveCategory] = useState('전체');

  const categories = ['전체', '인증서', '특허', '소프트웨어 등록증', '브로슈어'];

  const materials = [
    {
      id: 1,
      title: 'ISO 9001:2015 품질경영시스템 인증서',
      category: 'certificates',
      type: 'PDF',
      size: '2.3MB',
      date: '2024-01-15',
      description: '국제표준화기구(ISO) 품질경영시스템 인증서',
      downloadUrl: '#'
    },
    {
      id: 2,
      title: '국토교통부 신기술 제568호 지정서',
      category: 'certificates',
      type: 'PDF',
      size: '1.8MB',
      date: '2024-02-20',
      description: '지반조사 및 모니터링 신기술 지정서',
      downloadUrl: '#'
    },
    {
      id: 3,
      title: '지반 모니터링 시스템 특허증',
      category: 'patents',
      type: 'PDF',
      size: '3.2MB',
      date: '2023-11-15',
      description: '실시간 지반 모니터링 시스템 관련 특허 (특허 제10-2023-0123456호)',
      downloadUrl: '#'
    },
    {
      id: 4,
      title: '스마트 계측 센서 특허증',
      category: 'patents',
      type: 'PDF',
      size: '2.9MB',
      date: '2023-08-22',
      description: 'IoT 기반 스마트 계측 센서 기술 특허 (특허 제10-2023-0098765호)',
      downloadUrl: '#'
    },
    {
      id: 5,
      title: 'EGE 통합자동화 계측시스템 소프트웨어 등록증',
      category: 'software',
      type: 'PDF',
      size: '1.5MB',
      date: '2024-01-10',
      description: '통합자동화 계측시스템 소프트웨어 저작권 등록증 (C-2024-001234)',
      downloadUrl: '#'
    },
    {
      id: 6,
      title: 'GNSS 데이터 분석 프로그램 등록증',
      category: 'software',
      type: 'PDF',
      size: '1.3MB',
      date: '2023-12-05',
      description: 'GNSS 기반 데이터 분석 소프트웨어 등록증 (C-2023-005678)',
      downloadUrl: '#'
    },
    {
      id: 7,
      title: 'EGE 통합자동화 계측시스템 브로슈어',
      category: 'brochures',
      type: 'PDF',
      size: '5.2MB',
      date: '2024-03-10',
      description: '통합자동화 계측시스템 제품 소개 자료',
      downloadUrl: '#'
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCategoryClick = (category: string) => {
    setActiveCategory(category);
    scrollToSection('library-section');
  };

  const filteredMaterials = activeCategory === '전체' 
    ? materials 
    : materials.filter(material => material.category === activeCategory);

  const getFileIcon = (category: string) => {
    switch (category) {
      case 'patents':
        return 'ri-lightbulb-line';
      case 'software':
        return 'ri-code-line';
      case 'certificates':
        return 'ri-award-line';
      default:
        return 'ri-file-pdf-line';
    }
  };

  const getFileIconColor = (category: string) => {
    switch (category) {
      case 'patents':
        return 'text-purple-600';
      case 'software':
        return 'text-green-600';
      case 'certificates':
        return 'text-yellow-600';
      default:
        return 'text-red-600';
    }
  };

  const getFileIconBg = (category: string) => {
    switch (category) {
      case 'patents':
        return 'bg-purple-100';
      case 'software':
        return 'bg-green-100';
      case 'certificates':
        return 'bg-yellow-100';
      default:
        return 'bg-red-100';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* 주요 인증 및 등록 현황 섹션 - 첫 번째로 이동 */}
        <section className="py-20 bg-gray-50" id="certifications-section">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-6">주요 인증 및 등록 현황</h2>
                <p className="text-xl text-gray-600">EGE의 기술력을 인정받은 공식 인증서, 특허, 소프트웨어 등록증</p>
                <div className="w-24 h-1 bg-blue-600 mx-auto mt-6"></div>
              </div>

              <div className="grid lg:grid-cols-3 gap-12 mb-16">
                <div className="text-center" id="certificates-detail">
                  <button 
                    onClick={() => handleCategoryClick('인증서')}
                    className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6 hover:bg-yellow-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-award-line text-4xl text-yellow-600"></i>
                  </button>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">인증서</h3>
                  <div className="bg-white rounded-xl p-6">
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">ISO 9001:2015</h4>
                        <p className="text-sm text-gray-600">품질경영시스템 인증</p>
                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full mt-2 inline-block">유효</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">국토교통부 신기술</h4>
                        <p className="text-sm text-gray-600">제568호 지정</p>
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full mt-2 inline-block">등록</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">전문건설업 면허</h4>
                        <p className="text-sm text-gray-600">지반조성·포장공사업</p>
                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full mt-2 inline-block">유효</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center" id="patents-detail">
                  <button 
                    onClick={() => handleCategoryClick('특허')}
                    className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 hover:bg-purple-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-lightbulb-line text-4xl text-purple-600"></i>
                  </button>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">특허</h3>
                  <div className="bg-white rounded-xl p-6">
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">지반 모니터링 시스템</h4>
                        <p className="text-sm text-gray-600">특허 제10-2023-0123456호</p>
                        <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full mt-2 inline-block">등록</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">스마트 계측 센서</h4>
                        <p className="text-sm text-gray-600">특허 제10-2023-0098765호</p>
                        <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full mt-2 inline-block">등록</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">무선 데이터 전송</h4>
                        <p className="text-sm text-gray-600">특허 제10-2022-0087654호</p>
                        <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full mt-2 inline-block">등록</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center" id="software-detail">
                  <button 
                    onClick={() => handleCategoryClick('소프트웨어 등록증')}
                    className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 hover:bg-green-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-code-line text-4xl text-green-600"></i>
                  </button>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">소프트웨어 등록증</h3>
                  <div className="bg-white rounded-xl p-6">
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">통합자동화 계측시스템</h4>
                        <p className="text-sm text-gray-600">C-2024-001234</p>
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full mt-2 inline-block">등록</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">GNSS 데이터 분석 프로그램</h4>
                        <p className="text-sm text-gray-600">C-2023-005678</p>
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full mt-2 inline-block">등록</span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                        <h4 className="font-semibold text-gray-800 mb-2">실시간 모니터링 솔루션</h4>
                        <p className="text-sm text-gray-600">C-2023-009876</p>
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full mt-2 inline-block">등록</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-8 bg-blue-50 rounded-2xl p-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">15+</div>
                  <p className="text-gray-700">보유 인증서</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">8</div>
                  <p className="text-gray-700">등록 특허</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">12</div>
                  <p className="text-gray-700">소프트웨어 등록</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">20+</div>
                  <p className="text-gray-700">기술 문서</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 자료 라이브러리 바로가기 섹션 - 두 번째로 이동 */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-6">자료 라이브러리 바로가기</h2>
                <p className="text-xl text-gray-600">필요한 자료를 빠르게 찾아보세요</p>
                <div className="w-24 h-1 bg-blue-600 mx-auto mt-6"></div>
              </div>

              <div className="grid md:grid-cols-4 gap-8">
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-yellow-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-award-line text-2xl text-yellow-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">인증서</h3>
                  <p className="text-gray-600 mb-6">ISO 인증, 신기술 지정서 등 공식 인증 문서</p>
                  <button 
                    onClick={() => handleCategoryClick('인증서')}
                    className="text-blue-600 hover:text-blue-800 font-semibold whitespace-nowrap"
                  >
                    바로가기 →
                  </button>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-purple-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-lightbulb-line text-2xl text-purple-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">특허</h3>
                  <p className="text-gray-600 mb-6">혁신적인 기술 특허 및 실용신안 등록증</p>
                  <button 
                    onClick={() => handleCategoryClick('특허')}
                    className="text-blue-600 hover:text-blue-800 font-semibold whitespace-nowrap"
                  >
                    바로가기 →
                  </button>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-code-line text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">소프트웨어</h3>
                  <p className="text-gray-600 mb-6">자체 개발 소프트웨어 등록증 및 라이선스</p>
                  <button 
                    onClick={() => handleCategoryClick('소프트웨어 등록증')}
                    className="text-blue-600 hover:text-blue-800 font-semibold whitespace-nowrap"
                  >
                    바로가기 →
                  </button>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-file-text-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">브로슈어</h3>
                  <p className="text-gray-600 mb-6">제품 소개서, 회사 브로슈어 및 홍보 자료</p>
                  <button 
                    onClick={() => handleCategoryClick('브로슈어')}
                    className="text-blue-600 hover:text-blue-800 font-semibold whitespace-nowrap"
                  >
                    바로가기 →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 자료 라이브러리 섹션 - 세 번째로 이동 */}
        <section className="py-20 bg-gray-50" id="library-section">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-800 mb-6">
                  자료 라이브러리
                </h2>
                <p className="text-xl text-gray-600">
                  EGE의 모든 공식 자료를 카테고리별로 확인하세요
                </p>
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap justify-center gap-4 mb-12">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryClick(category)}
                    className={`px-6 py-3 rounded-full transition-colors whitespace-nowrap ${
                      activeCategory === category
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Materials Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredMaterials.map((material) => (
                  <div key={material.id} className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 ${getFileIconBg(material.category)} rounded-lg flex items-center justify-center`}>
                          <i className={`${getFileIcon(material.category)} text-2xl ${getFileIconColor(material.category)}`}></i>
                        </div>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {material.type}
                        </span>
                      </div>
                      
                      <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                        {material.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {material.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <span>{material.size}</span>
                        <span>{material.date}</span>
                      </div>
                      
                      <button className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center whitespace-nowrap">
                        <i className="ri-download-line mr-2"></i>
                        다운로드
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredMaterials.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-file-line text-3xl text-gray-400"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    해당 카테고리에 자료가 없습니다
                  </h3>
                  <p className="text-gray-500">
                    다른 카테고리를 선택하거나 새로운 자료를 업로드해주세요
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
